import { WithScope } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import { getServicePerTimeUnitLocalization } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import {
    createZodFormResolver,
    FormInputField,
    lt,
    NumberService,
    SubmitHandler,
    useFormControls,
} from '@mews/framework';
import { getCurrentCurrencyCultureInfo } from '@mews-commander/core/legacy-globalization';
import { UnicodeEntity } from '@mews/enums';
import { FormLayout } from '../../FormLayout';
import { getRateFormServiceId } from '../selectors';
import {
    RelativeAdjustmentRateFormKeys,
    relativeAdjustmentRateFormSchema,
    RelativeAdjustmentRateFormValues,
} from './types';
import { useRelativeAdjustmentRateMutation } from './useRelativeAdjustmentRateMutation';

interface RelativeAdjustmentRateFormContainerProps extends WithScope {
    reservationIds: string[];
    onSuccess?: () => void;
}

const FORM_PREFIX = 'relative-adjustment-rate-form-';

export const RelativeAdjustmentRateFormContainer = ({
    scope,
    reservationIds,
    onSuccess,
}: RelativeAdjustmentRateFormContainerProps) => {
    const serviceId = useCommanderSelector((state) => getRateFormServiceId(state, { scope })) ?? '';
    const timeUnitText = useCommanderSelector((state) => getServicePerTimeUnitLocalization(state, { id: serviceId }));
    const localizationInfo = useCommanderSelector(getCurrentCurrencyCultureInfo);

    const { mutate, isPending } = useRelativeAdjustmentRateMutation();
    const onSubmit: SubmitHandler<RelativeAdjustmentRateFormValues> = ({ reason, value }, { reset }) => {
        const unformattedValue = NumberService.unformat(value, localizationInfo) / 100;

        mutate(
            {
                reservationIds,
                reason,
                value: unformattedValue,
            },
            {
                onSuccess: () => {
                    reset();
                    onSuccess?.();
                },
            }
        );
    };

    // Creating the form control
    const { renderFormContent } = useFormControls<RelativeAdjustmentRateFormValues>({
        onSubmit,
        resolver: createZodFormResolver(relativeAdjustmentRateFormSchema(localizationInfo)),
        defaultValues: {
            [RelativeAdjustmentRateFormKeys.value]: '',
            [RelativeAdjustmentRateFormKeys.reason]: '',
        },
        dataTestForm: `${FORM_PREFIX}${scope}`,
    });

    return renderFormContent(<RelativeAdjustmentRateForm isLoading={isPending} timeUnitText={timeUnitText} />);
};

interface RelativeAdjustmentRateFormProps {
    isLoading: boolean;
    timeUnitText: string;
}

const RelativeAdjustmentRateForm = ({ isLoading, timeUnitText }: RelativeAdjustmentRateFormProps) => {
    return (
        <FormLayout
            title={lt('TimeUnitRelativeAdjustmentExcludingProducts', {
                timeUnit: timeUnitText,
            })}
            loading={isLoading}
            submitButtonLoading={isLoading}
        >
            <FormInputField
                name={RelativeAdjustmentRateFormKeys.value}
                required
                label={lt('Value')}
                suffix={UnicodeEntity.Percent}
            />
            <FormInputField
                name={RelativeAdjustmentRateFormKeys.reason}
                label={lt('ReservationDetailReasonInputLabel')}
                required
            />
        </FormLayout>
    );
};
